"use client";

// ── The Judge's editor column: four rows, one of them the editor (`EX-EDR01`) ─
// Wave 23 decision 1. A contestant's screenshot showed the verdict card and
// four "My attempts" rows squeezing the editor to three lines, because
// everything a submission produced was stacked under the editor in one
// scrolling column. Now the column is exactly four rows:
//
//   1. the toolbar (language, Reset, "Aa", the surface's own tools, the count)
//   2. the editor, which takes all the height left and never gives any back
//   3. the test strip (the caller's, one line plus a row of boxes at most)
//   4. the bottom bar (the verdict pill, the run slot, Submit)
//
// Whatever else a surface has to say floats over the editor as `overlay` (the
// result card) and never joins the column's flow. The one exception is the run
// panel (`EX-EDR02`), which sits between the editor and the strip and does take
// height, because the reader opened it, sized it, and closes it: a comparison
// is read beside the code it describes, not over it.
//
// `EX-EDR03` adds Upload file to the toolbar and a file drop on the editor:
// both open `UploadSourceDialog`, which reads and checks the file in the
// browser, and the file lands through `restore()`, one undo step, with a
// five-second Undo note over the editor (the canvas's place for it; a corner
// toast sat on Submit, the next thing the reader may press). Nothing about the
// file leaves the page but its text.
//
// **The course exercise does not use this.** It still composes
// `SolutionEditorPanel`, on the Playground's ground, sized to its content
// inside a scrolling lesson; this column only makes sense where the editor owns
// the pane. The two share `PlaygroundEditor`, the graded runtime list and the
// source bound, which are the parts that must not drift.

import { useEffect, useImperativeHandle, useRef, useState, type ClipboardEvent, type DragEvent, type ReactNode, type Ref } from "react";
import { Check, FileUp, Loader2, Lock, RotateCcw, Send, Undo2 } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PlaygroundEditor,
  type PlaygroundEditorHandle,
  type EditorMarker,
} from "@/features/playground/components/PlaygroundEditor";
import { useEditorSettingsState } from "@/features/playground/lib/use-editor-settings";
import {
  getRuntimeDefaultSourceCode,
  getRuntimeMonacoLanguage,
} from "@/features/quiz/lib/coding-task-format";
import { CODING_SOURCE_CODE_MAX } from "@/features/quiz/coding-task-api";
import { countLines } from "../../lib/source-file";
import { RuntimePicker, TextSettingsPanel, ToolbarPopover } from "./EditorToolbarControls";
import { UploadSourceDialog, type UploadedSource } from "./UploadSourceDialog";
import type { AllowedRuntime, SolutionDraft } from "./use-solution-draft";
import { isRunShortcut } from "./use-code-runs";

/**
 * `EX-FNC05`: a function problem's editor. Its template is the language's
 * starter (so Reset reads "Back to the starter"), and a language switch opens
 * on what was written in that language, else its starter, instead of
 * carrying the old language's text across.
 */
export type FunctionDrafts = {
  starter: (languageKey: string) => string;
  sourceFor: (languageKey: string) => string;
};

/** A drag that carries files from the desktop, not a selection dragged inside the page. */
function carriesFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

export function GradedEditorColumn({
  draft,
  onDraftChange,
  allowedRuntimes,
  runtimesLoading,
  runtimesFailed,
  toolbarEnd,
  pill,
  note,
  runSlot,
  submitLabel,
  submitting,
  onSubmit,
  action,
  error,
  strip,
  panel,
  overlay,
  onEditorKeyDown,
  onRunShortcut,
  onPaste,
  problemTitle,
  problemLabel,
  onUpload,
  uploadBlocked = false,
  contextMenu = true,
  markers,
  navigationRef,
  functionDrafts,
}: {
  draft: SolutionDraft | null;
  onDraftChange: (draft: SolutionDraft) => void;
  allowedRuntimes: AllowedRuntime[];
  runtimesLoading: boolean;
  runtimesFailed: boolean;
  /** The surface's own toolbar tools, after "Aa" (the contest's Print). */
  toolbarEnd?: ReactNode;
  /** The verdict pill, left of everything in the bottom bar. */
  pill: ReactNode;
  /** What a submission here counts towards, beside the pill where it fits. */
  note?: string;
  /** Run samples and Custom input (`EX-EDR02`, `RunSlotButtons`), on both pages. */
  runSlot?: ReactNode;
  submitLabel: string;
  submitting: boolean;
  /**
   * Submit. `EX-EDR03`: "Submit this file" passes the draft it just put in,
   * because the page's own `draft` has not re-rendered yet when the press that
   * changed it asks for the submit; Ctrl + Enter and the button pass nothing.
   */
  onSubmit: (draft?: SolutionDraft) => void;
  /**
   * Replaces Submit: a sign-in call, or the refusal the server already gave
   * (ended, not registered, staff), rendered where Submit would be.
   */
  action?: ReactNode;
  error?: string | null;
  /** The test strip, between the editor and the bar. */
  strip?: ReactNode;
  /** `EX-EDR02`: the run panel, between the editor and the strip, when open. */
  panel?: ReactNode;
  /** Floats over the lower editor: the result card. */
  overlay?: ReactNode;
  /** `EX-AUT06`: the contest's authorship meter, when its profile turns one on. */
  onEditorKeyDown?: (event: KeyboardEvent) => void;
  /** `EX-EDR02`: Ctrl + ' inside the editor runs (the samples, or the custom input). */
  onRunShortcut?: () => void;
  /** Measured and never cancelled: a contest editor does not block paste (`docs/29` §12). */
  onPaste?: (text: string) => void;
  /** `EX-EDR03`: under the upload dialog's title, "B · Pairs Divisible by K" or the practice title. */
  problemTitle: string;
  /** `EX-EDR03`: "Your editor has 10 lines for B": the letter, or the title. */
  problemLabel: string;
  /** `EX-EDR03`: told the text of a file that just went in, for the contest's meter. */
  onUpload?: (text: string) => void;
  /**
   * `EX-EDR03`, Wave 23 decision 8: the round blocks pasting, so uploading is off
   * too. The button stays and explains itself; a dropped file is refused.
   */
  uploadBlocked?: boolean;
  /** `EX-EDO01`: the editor's right-click menu; the Editor only level turns it off. */
  contextMenu?: boolean;
  markers?: readonly EditorMarker[];
  navigationRef?: Ref<Pick<PlaygroundEditorHandle, "revealLine">>;
  /** `EX-FNC05`: present only on a function problem. */
  functionDrafts?: FunctionDrafts;
}) {
  const t = useTranslations("judge.problem");
  const tEditor = useTranslations("judge.editor");
  const tUpload = useTranslations("judge.upload");
  const [editorSettings, setEditorSettings] = useEditorSettingsState();
  const editorRef = useRef<PlaygroundEditorHandle>(null);
  /**
   * `EX-FNC05`: the draft as this render has it. A language switch that also
   * swaps the text makes the editor `setValue` in a layout effect, and Monaco
   * reports that change through the handler it still holds from the render
   * before, whose `draft` is the old language: read through the ref, the
   * change lands on the draft it belongs to.
   */
  const draftRef = useRef(draft);
  draftRef.current = draft;
  useImperativeHandle(navigationRef, () => ({ revealLine: (line, column) => editorRef.current?.revealLine(line, column) ?? false }), []);
  /** `EX-EDR03`: the open upload dialog, and the files a drop opened it on. */
  const [upload, setUpload] = useState<{ files: File[] | null; opened: number } | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const dragDepth = useRef(0);
  /** `EX-EDR03`: "pairs.cpp is in the editor · Undo", for five seconds after a file goes in. */
  const [undoNote, setUndoNote] = useState<{ fileName: string; previous: SolutionDraft; shown: number } | null>(
    null,
  );
  useEffect(() => {
    if (!undoNote) return;
    const timer = window.setTimeout(() => setUndoNote(null), 5_000);
    return () => window.clearTimeout(timer);
  }, [undoNote]);

  const runtimesUnavailable = runtimesFailed || allowedRuntimes.length === 0;
  const template = draft
    ? functionDrafts
      ? functionDrafts.starter(draft.languageKey)
      : getRuntimeDefaultSourceCode(draft.languageKey)
    : "";
  const canSubmit = Boolean(draft?.sourceCode.trim()) && !runtimesUnavailable;
  const length = draft?.sourceCode.length ?? 0;

  // One Monaco key listener for both callers: the authorship meter sees every
  // key first, then Ctrl + ' runs. `undefined` when neither is wanted, so an
  // honour round without runs attaches nothing (`EX-AUT06`).
  const handleEditorKeyDown =
    onEditorKeyDown || onRunShortcut
      ? (event: KeyboardEvent) => {
          onEditorKeyDown?.(event);
          if (onRunShortcut && isRunShortcut(event)) {
            event.preventDefault();
            onRunShortcut();
          }
        }
      : undefined;

  const canUpload = Boolean(draft) && !runtimesUnavailable && !uploadBlocked;

  /**
   * The file goes in through `restore()`, one undo step, so Ctrl + Z brings the
   * old code back; then **one** draft change carrying the text and the
   * language. Monaco keeps its model across the language change, so the undo
   * stack survives it, and because the draft already holds the text, the value
   * sync never calls `setValue` (which would clear that stack).
   */
  const putIn = (source: UploadedSource, submit: boolean) => {
    if (!draft) return;
    const previous = draft;
    const next: SolutionDraft = {
      languageKey: source.runtime.languageKey,
      languageVersion: source.runtime.languageVersion,
      sourceCode: source.text,
    };
    editorRef.current?.restore(next.sourceCode);
    onDraftChange(next);
    onUpload?.(next.sourceCode);
    setUpload(null);
    setUndoNote({ fileName: source.fileName, previous, shown: Date.now() });
    if (submit) onSubmit(next);
  };

  /** Undo, the same way in: the old code and its language, as one more undo step. */
  const undoUpload = () => {
    if (!undoNote) return;
    editorRef.current?.restore(undoNote.previous.sourceCode);
    onDraftChange(undoNote.previous);
    setUndoNote(null);
  };

  /**
   * A file dragged over the editor: caught in the capture phase, before
   * Monaco's own drop handling on its node, and only when the drag carries
   * files, so dragging a selection inside the page still works as it did.
   */
  const dropHandlers = {
    onDragEnterCapture: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      dragDepth.current += 1;
      setDropActive(true);
    },
    onDragOverCapture: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = canUpload ? "copy" : "none";
    },
    onDragLeaveCapture: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.stopPropagation();
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDropActive(false);
    },
    onDropCapture: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      dragDepth.current = 0;
      setDropActive(false);
      if (!canUpload) return;
      setUpload({ files: Array.from(event.dataTransfer.files), opened: Date.now() });
    },
  };

  const resetToTemplate = () => {
    if (!draft) return;
    // One undo step through Monaco, so Ctrl+Z brings the reader's code back;
    // straight to the draft when the editor has not mounted yet.
    if (!editorRef.current?.restore(template)) {
      onDraftChange({ ...draft, sourceCode: template });
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-neutral-950" data-testid="editor-column">
      {/* 1. Toolbar */}
      <div
        className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-white/10 px-2 py-1.5 sm:px-3"
        data-testid="editor-toolbar"
      >
        {runtimesLoading ? (
          <span className="flex items-center gap-1.5 px-1 text-xs text-white/50">
            <Skeleton className="h-4 w-16 bg-white/10" />
            {t("runtimesLoading")}
          </span>
        ) : runtimesUnavailable ? (
          <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-300">
            {t("runtimesUnavailable")}
          </span>
        ) : draft ? (
          <RuntimePicker
            allowedRuntimes={allowedRuntimes}
            languageKey={draft.languageKey}
            languageVersion={draft.languageVersion}
            onChange={(next) => {
              // `EX-FNC05`: each language keeps its own draft on a function problem.
              if (functionDrafts) {
                onDraftChange({
                  ...draft,
                  ...next,
                  ...(next.languageKey !== draft.languageKey
                    ? { sourceCode: functionDrafts.sourceFor(next.languageKey) }
                    : {}),
                });
                return;
              }
              // `EX-PUB21`: a template nobody has touched follows the language,
              // so C to C++ no longer leaves C's Hello World behind. Anything
              // the reader wrote stays exactly as it is.
              const untouched = !draft.sourceCode.trim() || draft.sourceCode === template;
              const swap = untouched && next.languageKey !== draft.languageKey;
              onDraftChange({
                ...draft,
                ...next,
                ...(swap ? { sourceCode: getRuntimeDefaultSourceCode(next.languageKey) } : {}),
              });
            }}
          />
        ) : null}

        {/* `EX-EDR03`: Upload file, or why it is off in this round */}
        {uploadBlocked ? (
          <ToolbarPopover
            label={tUpload("blockedTitle")}
            align="start"
            testId="editor-upload-blocked"
            trigger={
              <>
                <Lock className="h-3.5 w-3.5" aria-hidden />
                <span className="sr-only sm:not-sr-only">{tUpload("button")}</span>
              </>
            }
          >
            <p className="text-xs leading-relaxed text-white/80">{tUpload("blocked")}</p>
          </ToolbarPopover>
        ) : (
          <Button
            type="button"
            variant="ghostDark"
            size="sm"
            onClick={() => setUpload({ files: null, opened: Date.now() })}
            disabled={!canUpload}
            title={tUpload("buttonTitle")}
            className="shrink-0"
            data-testid="editor-upload"
          >
            <FileUp className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only sm:not-sr-only">{tUpload("button")}</span>
          </Button>
        )}

        <Button
          type="button"
          variant="ghostDark"
          size="sm"
          onClick={resetToTemplate}
          disabled={!draft || draft.sourceCode === template}
          title={functionDrafts ? tEditor("resetStarterTitle") : tEditor("resetTitle")}
          className="shrink-0"
          data-testid="editor-reset"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          <span className="sr-only sm:not-sr-only">{functionDrafts ? tEditor("resetStarter") : tEditor("reset")}</span>
        </Button>

        <ToolbarPopover
          label={tEditor("settings")}
          trigger={<span className="px-0.5 text-sm font-semibold">{tEditor("settingsTrigger")}</span>}
          testId="editor-settings"
        >
          <TextSettingsPanel settings={editorSettings} onChange={setEditorSettings} />
        </ToolbarPopover>

        {toolbarEnd}

        {draft ? (
          <span className="ml-auto flex shrink-0 items-center gap-3 pl-2 text-xs">
            <span className="hidden items-center gap-1 text-white/45 md:inline-flex">
              <Check className="h-3.5 w-3.5" aria-hidden />
              {tEditor("draftSaved")}
            </span>
            <span
              className={cn(
                "tabular-nums",
                length > CODING_SOURCE_CODE_MAX * 0.9 ? "text-amber-300" : "text-white/55",
              )}
              data-testid="editor-char-count"
            >
              {tEditor("charCount", { count: length, max: CODING_SOURCE_CODE_MAX })}
            </span>
          </span>
        ) : null}
      </div>

      {/* 2. The editor, with whatever floats over it */}
      <div
        className="relative flex min-h-0 flex-1 flex-col"
        data-testid="editor-area"
        {...dropHandlers}
        {...(onPaste
          ? {
              // On a wrapper, because Monaco pastes into a hidden textarea of
              // its own and React's synthetic event bubbles out of it. Read,
              // never cancelled.
              onPaste: (event: ClipboardEvent) => onPaste(event.clipboardData?.getData("text") ?? ""),
            }
          : {})}
      >
        <PlaygroundEditor
          ref={editorRef}
          markers={markers}
          contextMenu={contextMenu}
          fillParent
          fillHeight
          language={getRuntimeMonacoLanguage(draft?.languageKey ?? "cpp")}
          value={draft?.sourceCode ?? ""}
          onChange={(source) => {
            const current = draftRef.current;
            if (!current) return;
            const bounded = source.length > CODING_SOURCE_CODE_MAX ? source.slice(0, CODING_SOURCE_CODE_MAX) : source;
            if (bounded === current.sourceCode) return;
            onDraftChange({ ...current, sourceCode: bounded });
          }}
          settings={editorSettings}
          onFontSizeChange={(fontSize) => setEditorSettings((s) => ({ ...s, fontSize }))}
          onRun={() => {
            if (!action && canSubmit && !submitting) onSubmit();
          }}
          {...(handleEditorKeyDown ? { onKeyDown: handleEditorKeyDown } : {})}
          ariaLabel={t("editorAriaLabel")}
          darkTheme="progsity-judge"
        />
        {overlay}
        {undoNote ? (
          <div
            role="status"
            key={undoNote.shown}
            className="absolute bottom-3 left-1/2 z-20 flex max-w-[calc(100%-1.5rem)] -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-neutral-800 py-1 pl-3.5 pr-1 text-sm text-white shadow-card"
            data-testid="editor-upload-toast"
          >
            <Check className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
            <span className="min-w-0 truncate">{tUpload("toast", { name: undoNote.fileName })}</span>
            <Button type="button" variant="linkDark" size="mini" onClick={undoUpload}>
              <Undo2 className="h-3.5 w-3.5" aria-hidden />
              {tUpload("undo")}
            </Button>
          </div>
        ) : null}
        {dropActive ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary bg-neutral-950/90 px-6 text-center"
            data-testid="editor-drop-overlay"
          >
            {canUpload ? (
              <>
                <FileUp className="h-7 w-7 text-accent-peach" aria-hidden />
                <p className="text-lg font-bold text-white">{tUpload("dropTitle")}</p>
                <p className="max-w-sm text-sm text-white/70">{tUpload("dropBody")}</p>
              </>
            ) : (
              <>
                <Lock className="h-7 w-7 text-white/60" aria-hidden />
                <p className="max-w-sm text-sm font-semibold text-white/85">
                  {uploadBlocked ? tUpload("blocked") : tUpload("dropUnavailable")}
                </p>
              </>
            )}
          </div>
        ) : null}
      </div>

      {upload && draft ? (
        <UploadSourceDialog
          key={upload.opened}
          onClose={() => setUpload(null)}
          allowedRuntimes={allowedRuntimes}
          current={{ languageKey: draft.languageKey, languageVersion: draft.languageVersion }}
          problemTitle={problemTitle}
          problemLabel={problemLabel}
          editorLines={countLines(draft.sourceCode)}
          initialFiles={upload.files}
          canSubmit={!action}
          submitting={submitting}
          onPut={putIn}
        />
      ) : null}

      {/* The run panel, when the reader has opened it (`EX-EDR02`) */}
      {panel}

      {/* 3. The test strip */}
      {strip}

      {/* 4. The bottom bar */}
      <div
        className="flex shrink-0 flex-wrap items-center gap-2 border-t border-white/10 bg-neutral-900 px-3 py-2.5"
        data-testid="editor-bar"
      >
        <div className="min-w-0 shrink">{pill}</div>
        {note ? <p className="hidden min-w-0 truncate text-xs text-white/45 xl:block">{note}</p> : null}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {runSlot}
          {action ?? (
            <Button
              type="button"
              onClick={() => onSubmit()}
              disabled={submitting || !canSubmit}
              title={tEditor("submitShortcutTitle")}
              className="h-10 rounded-full px-4 font-bold shadow-sm disabled:bg-white/10 disabled:text-white/40 disabled:opacity-100"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
              ) : (
                <Send className="h-4 w-4" aria-hidden />
              )}
              {submitLabel}
              <kbd
                aria-hidden
                className="hidden rounded bg-white/15 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-white/85 sm:inline">
                {tEditor("submitShortcut")}
              </kbd>
            </Button>
          )}
        </div>
        {error ? (
          <p
            role="alert"
            className="basis-full rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
          >
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
